import React, {useState} from "react"
import {Alert, Box, Button, CircularProgress, Typography} from "@mui/material"
import {useParams, useSearchParams} from "react-router-dom"

import {channelPresenter} from "./channelPresenter"
import {AssignButton} from "./components/AssignButton"
import {AssignDialog} from "./components/AssignDialog"
import {CandidateList, candidateCustomer} from "./components/CandidateCard"
import {CustomerSearch} from "./components/CustomerSearch"
import {DismissPanel} from "./components/DismissPanel"
import {IdentifyPanel} from "./components/IdentifyPanel"
import {ItemHeader} from "./components/ItemHeader"
import {OutcomeCard} from "./components/OutcomeCard"
import {Thread} from "./components/Thread"
import type {IdentifiableCustomer} from "./types"
import {useAssignCustomer, type AssignOutcome} from "./useAssignCustomer"
import {useUnidentifiedInbox} from "./useUnidentifiedInbox"

import {BackToSearch} from "@indebted/components/BackToSearch"
import {Layout} from "@indebted/components/Layout"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

const assignFailure: Record<Exclude<AssignOutcome, "assigned">, string> = {
	conflict: "A colleague assigned this message first.",
	forbidden: "You are not set up to assign messages in this inbox.",
	gone: "This message is no longer waiting to be identified.",
	rejected: "The assignment was refused.",
	failed: "Failed to assign the message.",
}

function PageFrame({children}: {children: React.ReactNode}) {
	const [search] = useSearchParams()

	return (
		<Layout>
			<Box px={3} pt={3} pb={4}>
				<Box mb={1}>
					<BackToSearch backURL={search.get("back") || "/inbox/unidentified"} />
				</Box>
				{children}
			</Box>
		</Layout>
	)
}

function ItemPage() {
	const {channel = "", conversationId = "", providerMessageId = ""} = useParams()
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const {data, loading, error, notFound, refetch} = useUnidentifiedInbox(
		channel,
		conversationId,
		providerMessageId,
		agentEmail,
	)
	const {assign, assigning} = useAssignCustomer(channel, conversationId, providerMessageId, agentEmail)
	const [addSenderEmail, setAddSenderEmail] = useState(false)
	const [pending, setPending] = useState<IdentifiableCustomer | null>(null)

	const confirmCustomer = (customer: IdentifiableCustomer) => {
		setAddSenderEmail(false)
		setPending(customer)
	}

	const assignPending = async () => {
		if (!pending) return

		const {outcome, reason} = await assign(pending.CustomerID, addSenderEmail)
		setPending(null)

		if (outcome === "assigned") {
			notification.success(`Assigned to ${pending.Name}.`)
			refetch()
			return
		}

		notification.error(reason || assignFailure[outcome])
		// A refused assignment can mean the page is stale rather than wrong, so
		// re-read and let the agent see whatever the message actually says now.
		if (outcome === "conflict" || outcome === "gone") refetch()
	}

	if (loading) {
		return (
			<PageFrame>
				<Box display="flex" justifyContent="center" p={3}>
					<CircularProgress />
				</Box>
			</PageFrame>
		)
	}

	if (notFound) {
		return (
			<PageFrame>
				<Typography variant="h6" component="h1" mb={1}>
					Message not found
				</Typography>
				<Typography variant="body2" color="textSecondary">
					This message is not in the unidentified inbox. It may have been identified already, or the link may
					point at another region.
				</Typography>
			</PageFrame>
		)
	}

	if (error || !data) {
		return (
			<PageFrame>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography color="error">{error || "Failed to load the message"}</Typography>
					<Button size="small" onClick={refetch}>
						Retry
					</Button>
				</Box>
			</PageFrame>
		)
	}

	if (!data.UnidentifiedInboxEnabled) {
		return (
			<PageFrame>
				<Alert severity="info">The unidentified inbox is not available for your account yet.</Alert>
			</PageFrame>
		)
	}

	const {Message: message, Thread: thread, Candidates: candidates} = data
	const presenter = channelPresenter(message.Channel)
	const isOpen = message.Status === "open"

	const assignButton = (customer: IdentifiableCustomer) => (
		<AssignButton name={customer.Name} onAssign={() => confirmCustomer(customer)} disabled={assigning} />
	)

	return (
		<PageFrame>
			<ItemHeader message={message} presenter={presenter} />
			<Box maxWidth={960}>
				<Thread
					messages={thread}
					threadSubject={message.Subject}
					senderAddress={message.From.Address}
					channel={channel}
					conversationID={conversationId}
				/>
				<Box mt={4}>
					<IdentifyPanel
						candidates={
							isOpen ? (
								<CandidateList
									customers={candidates.map(candidateCustomer)}
									title={
										candidates.length > 1 ? `${candidates.length} candidate records` : "Candidate"
									}
									empty="No candidate. Search for the customer to assign this message."
									renderActions={assignButton}
								/>
							) : undefined
						}
						search={
							isOpen ? (
								<CustomerSearch
									alreadyShown={candidates.map((candidate) => candidate.CustomerID)}
									renderActions={assignButton}
								/>
							) : undefined
						}
						outcome={
							!isOpen && message.Resolution ? <OutcomeCard resolution={message.Resolution} /> : undefined
						}
						actions={
							isOpen ? (
								<DismissPanel
									channel={message.Channel}
									conversationID={message.ConversationID}
									providerMessageID={message.ProviderMessageID}
									agentEmail={agentEmail}
									onDismissed={refetch}
								/>
							) : undefined
						}
					/>
				</Box>
			</Box>
			<AssignDialog
				customer={pending}
				senderAddress={message.From.Address}
				senderIsFormRelay={message.From.IsFormRelay}
				addSenderEmail={addSenderEmail}
				onAddSenderEmail={setAddSenderEmail}
				assigning={assigning}
				onCancel={() => setPending(null)}
				onConfirm={assignPending}
			/>
		</PageFrame>
	)
}

export {ItemPage}
